import { ButtonTypes } from "@/types/enums";
import clsx from "clsx";
import GradientWrapper from "../misc/Gradient";
import Loading from "../../icons/Loading";
import { resolveIcon, type IconInput } from "@/lib/resolveIcon";

type ButtonProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
> & {
    children: string;
    subtitle?: string;
    variant?: ButtonTypes;
    edgeIcon?: boolean;
    iconLeft?: IconInput;
    iconRight?: IconInput;
    loading?: boolean;
};

type VariantStyle = {
    outer: string;
    inner: string;
    text: string;
    gradient: string;
    spinner?: string;
};

const disabledFilled: VariantStyle = {
    outer: "bg-white shadow-soft gradient-border [--button-border:white]",
    inner: "",
    text: "text-button-text-disabled!",
    gradient: "bg-button-clean-disabled-gradient",
    spinner: "#D1D1D1",
};

const variantStyles: Record<ButtonTypes, { enabled: VariantStyle; disabled: VariantStyle }> = {
    [ButtonTypes.COLOR]: {
        enabled: {
            outer: "bg-yellow-500 gradient-border shadow-soft",
            inner: "bg-white/15 group-hover:bg-white/35 group-active:bg-white/35",
            text: "text-yellow-600",
            gradient: "bg-button-color-gradient",
            spinner: "#432500",
        },
        disabled: disabledFilled,
    },
    [ButtonTypes.CLEAN]: {
        enabled: {
            outer: "bg-white shadow-soft gradient-border [--button-border:white]",
            inner: "group-hover:bg-green-450/15 group-active:bg-green-450/15",
            text: "text-green-800",
            gradient: "bg-button-clean-gradient",
            spinner: "#004143",
        },
        disabled: disabledFilled,
    },
    [ButtonTypes.ALARM]: {
        enabled: {
            outer: "bg-white gradient-border [--button-border:var(--gradient-button-border-alarming)] shadow-soft",
            inner: "bg-white/15 group-hover:bg-white/35 group-active:bg-white/35",
            text: "text-magenta-800",
            gradient: "bg-gradient-alarming-linear",
            spinner: "#641931",
        },
        disabled: disabledFilled,
    },
    [ButtonTypes.OUTLINED]: {
        enabled: {
            outer: "border-2 border-green-800",
            inner: "group-hover:bg-green-800/10 group-active:bg-green-800/10",
            text: "text-green-800",
            gradient: "",
            spinner: "#004143",
        },
        disabled: { outer: "border-2 border-green-800 opacity-35", inner: "", text: "text-green-800", gradient: "", spinner: "#004143" },
    },
    [ButtonTypes.OUTLINED_WHITE]: {
        enabled: {
            outer: "border-2 border-white",
            inner: "group-hover:bg-white/15 group-active:bg-white/15",
            text: "text-white",
            gradient: "",
            spinner: "#FFFFFF",
        },
        disabled: { outer: "border-2 border-white opacity-35", inner: "", text: "text-white", gradient: "", spinner: "#FFFFFF" },
    },
};

const Button = ({
    children,
    subtitle,
    variant = ButtonTypes.CLEAN,
    edgeIcon = true,
    iconLeft,
    iconRight,
    disabled,
    loading,
    className,
    ...rest
}: ButtonProps) => {

    const style = variantStyles[variant][disabled ? "disabled" : "enabled"];

    const leftIconNode = resolveIcon(iconLeft);
    const rightIconNode = resolveIcon(iconRight);

    return (
        <button
            type="button"
            className={clsx(
                "group relative rounded-2xl w-full h-15 min-h-15 overflow-hidden",
                disabled || loading ? "cursor-not-allowed" : "cursor-pointer",
                "focus-visible:outline-none focus-visible:border-button-focus-border focus-visible:[--button-border:var(--color-button-focus-border)]",
                style.outer,
                className
            )}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...rest}
        >
            <GradientWrapper gradient={style.gradient} />
            <span className={clsx(
                "z-10 relative flex justify-center items-center gap-4 p-4 h-15 font-18-bold transition-colors duration-200 ease-out",
                edgeIcon && (leftIconNode || rightIconNode) && "px-12",
                style.inner,
                style.text
            )}>
                {leftIconNode ? <span aria-hidden className={clsx(edgeIcon && "left-4 absolute", loading && "invisible")}>{leftIconNode}</span> : null}
                <span>
                    <span>
                        {loading ?
                            <>
                                <span className="inline-block animate-spin">
                                    <Loading color={style.spinner} />
                                </span>
                                <span className="sr-only">{children}</span>
                            </> :
                            children
                        }
                    </span>
                    {subtitle && !loading &&
                        <span className="block font-12-semiBold">{subtitle}</span>
                    }
                </span>
                {rightIconNode ? <span aria-hidden className={clsx(edgeIcon && "right-4 absolute", loading && "invisible")}>{rightIconNode}</span> : null}
            </span>
        </button>
    )
}

export default Button
